import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';
import { currentMonthKeySingapore, formatTaskMonthLabel } from '../utils/taskHelpers';

export type MonthlyVisitationItem = {
  id: string;
  owner_id: string;
  month_key: string;
  /** Null for prospects not yet opened as CRM accounts. */
  client_id: string | null;
  /** Display / prospect name; filled for new customers without an account. */
  company_name: string | null;
  visited: boolean;
  visited_at: string | null;
  task_id: string | null;
  sort_order: number;
  created_at?: string;
};

function mapRow(row: Record<string, unknown>): MonthlyVisitationItem {
  return {
    id: String(row.id),
    owner_id: String(row.owner_id ?? ''),
    month_key: String(row.month_key ?? ''),
    client_id: row.client_id != null && String(row.client_id).trim() !== '' ? String(row.client_id) : null,
    company_name:
      row.company_name != null && String(row.company_name).trim() !== ''
        ? String(row.company_name).trim()
        : null,
    visited: Boolean(row.visited),
    visited_at: row.visited_at != null ? String(row.visited_at) : null,
    task_id: row.task_id != null ? String(row.task_id) : null,
    sort_order: Number(row.sort_order ?? 0),
    created_at: row.created_at != null ? String(row.created_at) : undefined,
  };
}

function migrationHint(error: { code?: string }): Error | null {
  if (error.code === '42P01') {
    return new Error(
      'Table monthly_visitation_items not found. Run supabase/migrations/090_monthly_visitation_list.sql in Supabase.',
    );
  }
  if (error.code === '42703' || error.code === '23502') {
    return new Error(
      'Visitation list needs prospect support. Run supabase/migrations/099_monthly_visitation_prospects.sql in Supabase.',
    );
  }
  if (error.code === '42P10' || /no unique|ON CONFLICT/i.test(String((error as { message?: string }).message ?? ''))) {
    return new Error(
      'Visitation unique index missing or incompatible. Run supabase/migrations/099_monthly_visitation_prospects.sql in Supabase, then refresh.',
    );
  }
  return null;
}

export async function fetchMonthlyVisitationItems(
  ownerId: string,
  monthKey: string,
): Promise<MonthlyVisitationItem[]> {
  const { data, error } = await getDbSupabase()
    .from('monthly_visitation_items')
    .select('*')
    .eq('owner_id', ownerId)
    .eq('month_key', monthKey)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    throw migrationHint(error) ?? new Error(formatSupabaseError(error));
  }

  return (data ?? []).map((row) => mapRow(row as Record<string, unknown>));
}

export async function addMonthlyVisitationItems(input: {
  ownerId: string;
  monthKey: string;
  clientIds: string[];
  startSortOrder?: number;
}): Promise<{ added: number; skipped: number }> {
  const ids = [...new Set(input.clientIds.map((id) => String(id).trim()).filter(Boolean))];
  if (ids.length === 0) return { added: 0, skipped: 0 };

  // Avoid PostgREST ON CONFLICT: partial unique indexes (migration 099) are not
  // accepted as onConflict targets → "no unique or exclusion constraint…".
  const { data: existingRows, error: existingError } = await getDbSupabase()
    .from('monthly_visitation_items')
    .select('client_id')
    .eq('owner_id', input.ownerId)
    .eq('month_key', input.monthKey)
    .in('client_id', ids);

  if (existingError) {
    throw migrationHint(existingError) ?? new Error(formatSupabaseError(existingError));
  }

  const already = new Set(
    (existingRows ?? [])
      .map((row) => (row.client_id != null ? String(row.client_id) : ''))
      .filter(Boolean),
  );
  const toAdd = ids.filter((id) => !already.has(id));
  if (toAdd.length === 0) return { added: 0, skipped: ids.length };

  const start = input.startSortOrder ?? 0;
  const rows = toAdd.map((clientId, index) => ({
    owner_id: input.ownerId,
    month_key: input.monthKey,
    client_id: clientId,
    company_name: null as string | null,
    sort_order: start + index,
  }));

  const { data, error } = await getDbSupabase()
    .from('monthly_visitation_items')
    .insert(rows)
    .select('id');

  if (error) {
    // Race: another tab inserted some rows — insert remaining one-by-one.
    if (error.code === '23505') {
      let added = 0;
      for (const row of rows) {
        const { error: insertError } = await getDbSupabase()
          .from('monthly_visitation_items')
          .insert(row);
        if (!insertError) {
          added += 1;
          continue;
        }
        if (insertError.code === '23505') continue;
        throw migrationHint(insertError) ?? new Error(formatSupabaseError(insertError));
      }
      return { added, skipped: Math.max(0, ids.length - added) };
    }
    throw migrationHint(error) ?? new Error(formatSupabaseError(error));
  }

  const added = data?.length ?? 0;
  return { added, skipped: Math.max(0, ids.length - added) };
}

/** Add a prospect (no CRM account yet) by company name. */
export async function addMonthlyVisitationProspect(input: {
  ownerId: string;
  monthKey: string;
  companyName: string;
  sortOrder?: number;
}): Promise<MonthlyVisitationItem> {
  const name = input.companyName.trim();
  if (!name) throw new Error('Company name is required.');

  const { data, error } = await getDbSupabase()
    .from('monthly_visitation_items')
    .insert({
      owner_id: input.ownerId,
      month_key: input.monthKey,
      client_id: null,
      company_name: name,
      sort_order: input.sortOrder ?? 0,
    })
    .select('*')
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('That company is already on this month’s list.');
    }
    throw migrationHint(error) ?? new Error(formatSupabaseError(error));
  }
  return mapRow(data as Record<string, unknown>);
}

export async function removeMonthlyVisitationItem(id: string): Promise<void> {
  const { error } = await getDbSupabase().from('monthly_visitation_items').delete().eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function setMonthlyVisitationVisited(
  id: string,
  visited: boolean,
): Promise<MonthlyVisitationItem> {
  const { data, error } = await getDbSupabase()
    .from('monthly_visitation_items')
    .update({
      visited,
      visited_at: visited ? new Date().toISOString() : null,
    })
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw new Error(formatSupabaseError(error));
  return mapRow(data as Record<string, unknown>);
}

export async function linkMonthlyVisitationTask(
  id: string,
  taskId: string,
  clientId?: string | null,
): Promise<MonthlyVisitationItem> {
  const patch: Record<string, unknown> = { task_id: taskId };
  if (clientId != null && String(clientId).trim() !== '') {
    patch.client_id = String(clientId);
  }
  const { data, error } = await getDbSupabase()
    .from('monthly_visitation_items')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw new Error(formatSupabaseError(error));
  return mapRow(data as Record<string, unknown>);
}

/** Current month + next 2 months for planning ahead. */
export function visitationMonthOptions(): { value: string; label: string }[] {
  const start = currentMonthKeySingapore();
  const [yearRaw, monthRaw] = start.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!year || !month) return [{ value: start, label: formatTaskMonthLabel(start) }];

  const options: { value: string; label: string }[] = [];
  for (let i = 0; i < 3; i += 1) {
    const d = new Date(year, month - 1 + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    options.push({ value, label: formatTaskMonthLabel(value) });
  }
  return options;
}

export function visitationItemDisplayName(
  item: MonthlyVisitationItem,
  accountName?: string | null,
): string {
  if (accountName?.trim()) return accountName.trim();
  if (item.company_name?.trim()) return item.company_name.trim();
  if (item.client_id) return `Account #${item.client_id}`;
  return 'New customer';
}
